// Contactes.js
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useUserContext } from "../../utils/UserContext";
import { tincPermis } from "../../utils/Permisos";
import { mapToPresentable, mapejos } from "../../utils/Mapejos";
import { API_URL } from "../../utils/config";
import TeclaEscEnter from "../../utils/Teclaescenter";
import { validarEmail, validarTelefon } from "../../utils/Validacions";
import AjudaEntitats from "./AjudaEntitats";
import { BotoAjuda } from "./AjudaModal";

function idContacte(c) {
  return c ? Number(c.idContactes) : null;
}

function Contactes({ tipusPare, entitat, onCancel, subnav }) {
  const esProveidor = tipusPare === "Proveïdor" || tipusPare === "Proveidor";
  const etiquetaPare = esProveidor ? "proveïdor" : "client";
  // table | nou | consultar | modificar | esborrar
  const [activeTab, setActiveTab] = useState("table");
  const [contactes, setContactes] = useState([]); // tots els contactes de l'empresa
  const [relacionsContactesEntitats, setRelacionsContactesEntitats] = useState({}); // mapa: idContacte -> [idEntitat, ...]
  const [selectedContact, setSelectedContact] = useState(null);
  const [menuObert, setMenuObert] = useState(null);
  const [menuPos, setMenuPos] = useState(null);
  const [menuAccions, setMenuAccions] = useState(false);
  const [mostraAjuda, setMostraAjuda] = useState(false);
  const [isNarrow, setIsNarrow] = useState(false);
  const menuRef = useRef(null);

  const { empresaSeleccionada, rolUsuari } = useUserContext();
  const idEmpresa = empresaSeleccionada?.IdEmpresa ?? null;
  const pucCrear = tincPermis(rolUsuari, "CREAR");
  const pucConsultar = tincPermis(rolUsuari, "CONSULTAR");
  const pucModificar = tincPermis(rolUsuari, "MODIFICAR");
  const pucEsborrar = tincPermis(rolUsuari, "ESBORRAR");

  // ESC: subpàgina → taula; taula → onCancel
  const handleEsc = useCallback(() => {
    if (activeTab === "table") onCancel?.();
    else setActiveTab("table");
  }, [activeTab, onCancel]);
  TeclaEscEnter(handleEsc, null);

  const entitatId = entitat?.Entitats_idEntitats ?? null;

  const fetchContactes = async () => {
    if (!idEmpresa) return;
    try {
      const res = await fetch(`${API_URL}/Contactesagenda.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          idEmpresa: idEmpresa,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setContactes(Array.isArray(data.contactes) ? data.contactes : []);
        setRelacionsContactesEntitats(data.relacionsContactesEntitats || {});
      } else {
        setContactes([]);
        setRelacionsContactesEntitats({});
      }
    } catch {
      setContactes([]);
      setRelacionsContactesEntitats({});
    }
  };

  // Filtrar contactes vinculats a l'entitat actual + per Rol
  const contactesFiltrats = useMemo(() => {
    if (!entitatId) return [];

    return contactes.filter((c) => {
      const idContacte = String(c.idContactes);
      const entitatsIds = relacionsContactesEntitats[idContacte] || [];
      if (!entitatsIds.includes(entitatId)) return false;

      const rol = c.Rol || "";

      if (tipusPare === "Client") {
        return rol === "Client" || rol === "Ambdos";
      } else if (tipusPare === "Proveïdor" || tipusPare === "Proveidor") {
        return rol === "Proveïdor" || rol === "Proveidor" || rol === "Ambdos";
      } else if (tipusPare === "Altres") {
        return rol === "Altres";
      }

      return true;
    });
  }, [contactes, relacionsContactesEntitats, entitatId, tipusPare]);

  useEffect(() => {
    setActiveTab("table");
    setSelectedContact(null);
    fetchContactes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idEmpresa]);

  const reFetchToTable = () => {
    fetchContactes();
    setActiveTab("table");
    setSelectedContact(null);
  };

  const renderEntitatHeader = () => {
    if (!entitat) return null;

    const tipus = mapToPresentable(entitat.TipusEntitat, mapejos.tipusEntitat);
    const isEmpresa = ["Empresa", "Autonom"].includes(tipus);
    const idLabel = ["Particular", "Autonom"].includes(tipus) ? "NIF" : "CIF";

    const cognoms = [entitat.Cognom1, entitat.Cognom2].filter(Boolean).join(" ");
    const nomComplet = ["Empresa", "Autonom"].includes(tipus)
      ? (entitat.NomComercial?.trim() ? entitat.NomComercial : entitat.Nom)
      : [cognoms, entitat.Nom].filter((x) => x && x.trim()).join(", ");

    return (
      <>
        <div className="Dashboard-form-section">
          <div className="info-inline"><strong>Organització:</strong> {tipusPare || "—"}</div>
          <div className="info-inline"><strong>Nom:</strong> {nomComplet || "—"}</div>
          {isEmpresa && (
            <div className="info-inline"><strong>Nom Comercial:</strong> {entitat.NomComercial || "—"}</div>
          )}
          <div className="info-inline"><strong>{idLabel}:</strong> {entitat.CIF_DNI || "—"}</div>
        </div>
      </>
    );
  };

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const apply = () => setIsNarrow(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    const tancar = (e) => {
      if (e.target.closest(".dv2-menu") || e.target.closest(".dv2-menuBtn")) return;
      setMenuObert(null);
    };
    document.addEventListener("mousedown", tancar);
    return () => document.removeEventListener("mousedown", tancar);
  }, []);

  useEffect(() => {
    if (activeTab !== "consultar") setMenuAccions(false);
  }, [activeTab]);

  const obrirFitxa = (contacte, mode) => {
    setMenuObert(null);
    setSelectedContact(contacte);
    setActiveTab(mode);
  };

  const obrirMenu = (e, contacte) => {
    e.stopPropagation();
    const id = idContacte(contacte);
    if (menuObert === id) {
      setMenuObert(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const ample = 200;
    const alt = 160;
    const marge = 8;
    let top = rect.bottom + 4;
    if (top + alt > window.innerHeight - marge) {
      top = Math.max(marge, rect.top - alt - 4);
    }
    let left = rect.right - ample;
    if (left < marge) left = marge;
    if (left + ample > window.innerWidth - marge) {
      left = window.innerWidth - ample - marge;
    }
    setMenuPos({ top, left });
    setMenuObert(id);
  };

  const renderAccions = (contacte) => (
    <div className="dv2-table-actions">
      <button
        type="button"
        className={`dv2-menuBtn ${menuObert === idContacte(contacte) ? "is-open" : ""}`}
        aria-label="Accions"
        onClick={(e) => obrirMenu(e, contacte)}
      >
        …
      </button>
    </div>
  );

  const nomEntitat = (() => {
    if (!entitat) return "";
    const tipus = mapToPresentable(entitat.TipusEntitat, mapejos.tipusEntitat);
    const cognoms = [entitat.Cognom1, entitat.Cognom2].filter(Boolean).join(" ");
    if (["Empresa", "Autònom", "Autonom"].includes(tipus) || ["Empresa", "Autonom"].includes(entitat.TipusEntitat)) {
      return entitat.NomComercial?.trim() ? entitat.NomComercial : entitat.Nom;
    }
    return [cognoms, entitat.Nom].filter((x) => x && String(x).trim()).join(", ");
  })();

  const renderTaula = () => {
    const files = contactesFiltrats.map((c) => {
      const cognoms = [c.Cognom1, c.Cognom2].filter(Boolean).join(" ");
      const nomFila = [cognoms, c.Nom].filter((x) => x && x.trim()).join(", ");
      const isSelected = selectedContact?.idContactes === c.idContactes;
      return { c, nomFila, isSelected };
    });

    if (subnav) {
      const contacteMenu = menuObert ? files.find(({ c }) => idContacte(c) === menuObert)?.c : null;
      return (
        <section className="dv2-card">
          {files.length === 0 ? (
            <p className="dv2-empty">No hi ha contactes vinculats a aquest {etiquetaPare}.</p>
          ) : isNarrow ? (
            <div className="dv2-recordList" style={{ display: "flex" }}>
              {files.map(({ c, nomFila, isSelected }) => (
                <article
                  key={c.idContactes}
                  className={`dv2-record ${isSelected ? "is-selected" : ""}`}
                  onClick={() => setSelectedContact(isSelected ? null : c)}
                  onDoubleClick={() => pucConsultar && obrirFitxa(c, "consultar")}
                >
                  <div>
                    <p className="dv2-record-title">{nomFila || "—"}</p>
                    <p className="dv2-record-meta">{[c.Telefon, c.Email].filter(Boolean).join(" · ") || "—"}</p>
                  </div>
                  {renderAccions(c)}
                </article>
              ))}
            </div>
          ) : (
            <div className="dv2-tableWrap">
              <table className="dv2-table">
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Telèfon</th>
                    <th>Email</th>
                    <th className="dv2-table-actions">Accions</th>
                  </tr>
                </thead>
                <tbody>
                  {files.map(({ c, nomFila, isSelected }) => (
                    <tr
                      key={c.idContactes}
                      className={isSelected ? "is-selected" : ""}
                      onClick={() => setSelectedContact(isSelected ? null : c)}
                      onDoubleClick={() => pucConsultar && obrirFitxa(c, "consultar")}
                    >
                      <td>{nomFila || "—"}</td>
                      <td>{c.Telefon || "—"}</td>
                      <td>{c.Email || "—"}</td>
                      <td className="dv2-table-actions">{renderAccions(c)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {contacteMenu &&
            menuPos &&
            createPortal(
              <div className="dv2-menu dv2-menu--fixed" ref={menuRef} style={{ top: menuPos.top, left: menuPos.left }}>
                {pucConsultar && (
                  <button type="button" className="dv2-menu-item" onClick={() => obrirFitxa(contacteMenu, "consultar")}>
                    Consultar
                  </button>
                )}
                {pucModificar && (
                  <button type="button" className="dv2-menu-item" onClick={() => obrirFitxa(contacteMenu, "modificar")}>
                    Modificar
                  </button>
                )}
                {pucEsborrar && (
                  <button
                    type="button"
                    className="dv2-menu-item is-danger"
                    onClick={() => obrirFitxa(contacteMenu, "esborrar")}
                  >
                    Esborrar
                  </button>
                )}
              </div>,
              document.body
            )}
        </section>
      );
    }

    return (
      <>
        <div className="Dashboard-table-container">
          {contactesFiltrats.length === 0 ? (
            <p>No hi ha contactes vinculats a aquesta entitat.</p>
          ) : (
            <table className="Dashboard-table">
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>Telèfon</th>
                  <th>Email</th>
                </tr>
              </thead>
              <tbody>
                {files.map(({ c, nomFila, isSelected }) => (
                  <tr
                    key={c.idContactes}
                    className={isSelected ? "selected-row" : ""}
                    onClick={() => setSelectedContact(isSelected ? null : c)}
                    onDoubleClick={() => {
                      setSelectedContact(c);
                      setActiveTab("consultar");
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <td>{nomFila || "—"}</td>
                    <td>{c.Telefon || "—"}</td>
                    <td>{c.Email || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="Dashboard-button-group">
          <button className="Dashboard-btn Dashboard-btn-cancelar" onClick={onCancel}>
            Tornar
          </button>
        </div>
      </>
    );
  };

  const hasSel = !!selectedContact;
  const renderTabs = () => (
    <div className="Dashboard-tabs">
      <button
        className={`Dashboard-tab ${activeTab === "nou" ? "active" : ""}`}
        onClick={() => { setActiveTab("nou"); setSelectedContact(null); }}
      >
        Nou
      </button>
      <button
        className={`Dashboard-tab ${activeTab === "consultar" ? "active" : ""} ${!hasSel ? "disabled" : ""}`}
        disabled={!hasSel}
        onClick={() => hasSel && setActiveTab("consultar")}
      >
        Consultar
      </button>
      <button
        className={`Dashboard-tab ${activeTab === "modificar" ? "active" : ""} ${!hasSel ? "disabled" : ""}`}
        disabled={!hasSel}
        onClick={() => hasSel && setActiveTab("modificar")}
      >
        Modificar
      </button>
      <button
        className={`Dashboard-tab ${activeTab === "esborrar" ? "active" : ""} ${!hasSel ? "disabled" : ""}`}
        disabled={!hasSel}
        onClick={() => hasSel && setActiveTab("esborrar")}
      >
        Esborrar
      </button>
    </div>
  );

  const canNavigate = useMemo(
    () => contactesFiltrats.length > 1 && !!selectedContact,
    [contactesFiltrats.length, selectedContact]
  );
  const goPrev = () => {
    if (!selectedContact) return;
    const idx = contactesFiltrats.findIndex((c) => c.idContactes === selectedContact.idContactes);
    if (idx < 0) return;
    const prev = idx === 0 ? contactesFiltrats.length - 1 : idx - 1;
    setSelectedContact(contactesFiltrats[prev]);
  };
  const goNext = () => {
    if (!selectedContact) return;
    const idx = contactesFiltrats.findIndex((c) => c.idContactes === selectedContact.idContactes);
    if (idx < 0) return;
    const next = idx === contactesFiltrats.length - 1 ? 0 : idx + 1;
    setSelectedContact(contactesFiltrats[next]);
  };

  const detallsV2 = !!subnav;
  const idxConsultar = selectedContact
    ? contactesFiltrats.findIndex((c) => c.idContactes === selectedContact.idContactes)
    : -1;

  const renderActive = () => {
    switch (activeTab) {
      case "table":
        return renderTaula();

      case "nou":
        return (
          <DetallsContacte
            v2={detallsV2}
            mode="nou"
            entitat={entitat}
            tipusPare={tipusPare}
            onSave={reFetchToTable}
            onCancel={() => setActiveTab("table")}
          />
        );

      case "consultar":
        if (!selectedContact) return null;
        return (
          <>
            <DetallsContacte
              v2={detallsV2}
              mode="consultar"
              entitat={entitat}
              tipusPare={tipusPare}
              contact={selectedContact}
              onCancel={() => setActiveTab("table")}
              onPrev={canNavigate ? goPrev : undefined}
              onNext={canNavigate ? goNext : undefined}
              navIndex={idxConsultar >= 0 ? idxConsultar + 1 : 0}
              navTotal={contactesFiltrats.length}
            />
            {!detallsV2 && (
              <div className="Dashboard-button-group">
                {canNavigate && (
                  <button type="button" className="Dashboard-btn Dashboard-btn-navegar" onClick={goPrev}>
                    &#x276E;
                  </button>
                )}
                <button type="button" className="Dashboard-btn Dashboard-btn-cancelar" onClick={() => setActiveTab("table")}>
                  Tornar
                </button>
                {canNavigate && (
                  <button type="button" className="Dashboard-btn Dashboard-btn-navegar" onClick={goNext}>
                    &#x276F;
                  </button>
                )}
              </div>
            )}
          </>
        );

      case "modificar":
        if (!selectedContact) return null;
        return (
          <DetallsContacte
            v2={detallsV2}
            mode="modificar"
            entitat={entitat}
            tipusPare={tipusPare}
            contact={selectedContact}
            onSave={reFetchToTable}
            onCancel={() => setActiveTab("table")}
          />
        );

      case "esborrar":
        if (!selectedContact) return null;
        return (
          <DetallsContacte
            v2={detallsV2}
            mode="esborrar"
            entitat={entitat}
            tipusPare={tipusPare}
            contact={selectedContact}
            onSave={reFetchToTable}
            onCancel={() => setActiveTab("table")}
          />
        );

      default:
        return null;
    }
  };

  const titolContactes =
    activeTab === "nou"
      ? "Nou contacte"
      : activeTab === "esborrar"
      ? "Esborrar contacte"
      : activeTab === "modificar"
      ? "Modificar contacte"
      : activeTab === "consultar"
      ? "Contactes · Fitxa"
      : "Contactes";

  return subnav ? (
    <div className="dv2-entity">
      {mostraAjuda && <AjudaEntitats pestanya="contactes" onTancar={() => setMostraAjuda(false)} />}
      <div className="dv2-pageHeader">
        <div className="dv2-pageHeader-main">
          <div className={`dv2-pageHeader-icon ${activeTab === "esborrar" ? "is-danger" : ""}`} aria-hidden="true">
            {activeTab === "esborrar" ? "🗑️" : "👥"}
          </div>
          <div>
            <h1 className="dv2-pageHeader-title">{titolContactes}</h1>
            <p className="dv2-pageHeader-subtitle">{nomEntitat || `Contactes del ${etiquetaPare}`}</p>
          </div>
        </div>
        <div className="dv2-pageHeader-actions">
          <BotoAjuda title="Com funcionen els Contactes" onClick={() => setMostraAjuda(true)} />
          <button
            type="button"
            className="dv2-btn"
            onClick={activeTab === "table" ? onCancel : () => setActiveTab("table")}
          >
            Tornar
          </button>
          {activeTab === "table" && pucCrear && (
            <button
              type="button"
              className="dv2-btn dv2-btn--primary"
              onClick={() => {
                setSelectedContact(null);
                setActiveTab("nou");
              }}
            >
              Nou contacte
            </button>
          )}
          {activeTab === "consultar" && pucModificar && (
            <button type="button" className="dv2-btn dv2-btn--primary" onClick={() => setActiveTab("modificar")}>
              Modificar
            </button>
          )}
          {activeTab === "consultar" && pucEsborrar && (
            <div style={{ position: "relative" }}>
              <button type="button" className="dv2-btn" onClick={() => setMenuAccions((v) => !v)}>
                Més accions
              </button>
              {menuAccions && (
                <div className="dv2-menu" style={{ right: 0, top: "42px" }}>
                  <button
                    type="button"
                    className="dv2-menu-item is-danger"
                    onClick={() => {
                      setMenuAccions(false);
                      setActiveTab("esborrar");
                    }}
                  >
                    Esborrar
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {subnav}
      {activeTab === "table" ? <div className="dv2-entity-body">{renderTaula()}</div> : renderActive()}
    </div>
  ) : (
    <>
      {renderEntitatHeader()}
      {renderTabs()}
      {renderActive()}
    </>
  );
}

/* ============================ DetallsContacte ============================ */

function DetallsContacte({
  mode,
  contact,
  entitat,
  tipusPare,
  onSave,
  onCancel,
  v2,
  onPrev,
  onNext,
  navIndex,
  navTotal,
}) {
  const esProveidor = tipusPare === "Proveïdor" || tipusPare === "Proveidor";
  const etiquetaPare = esProveidor ? "proveïdor" : "client";
  const rolPare = esProveidor ? "Proveidor" : "Client";
  const isReadOnly = mode === "consultar" || mode === "esborrar";

  const [nom, setNom] = useState("");
  const [cognom1, setCognom1] = useState("");
  const [cognom2, setCognom2] = useState("");
  const [telefon, setTelefon] = useState("");
  const [email, setEmail] = useState("");

  const [showModalSimilar, setShowModalSimilar] = useState(false);
  const [contacteSimilar, setContacteSimilar] = useState(null);
  const [tipusCoincidenciaSimilar, setTipusCoincidenciaSimilar] = useState("");

  const idEntitat = entitat?.Entitats_idEntitats ?? null;
  const { empresaSeleccionada } = useUserContext();
  const idEmpresa = empresaSeleccionada?.IdEmpresa ?? null;

  useEffect(() => {
    if (contact) {
      setNom(contact.Nom || "");
      setCognom1(contact.Cognom1 || "");
      setCognom2(contact.Cognom2 || "");
      setTelefon(contact.Telefon || "");
      setEmail(contact.Email || "");
    } else {
      setNom("");
      setCognom1("");
      setCognom2("");
      setTelefon("");
      setEmail("");
    }
  }, [contact]);

  const handleSubmit = async () => {
    if (
      (!nom || nom.trim() === "") &&
      (!cognom1 || cognom1.trim() === "") &&
      (!cognom2 || cognom2.trim() === "") &&
      mode !== "esborrar"
    ) {
      alert("Cal omplir almenys un d'aquests camps: Nom, Cognom1 o Cognom2.");
      return;
    }

    if (mode !== "esborrar") {
      const tel = validarTelefon(telefon);
      if (!tel.ok) {
        alert(`Telèfon: ${tel.missatge}`);
        return;
      }
      const mail = validarEmail(email);
      if (!mail.ok) {
        alert(`Email: ${mail.missatge}`);
        return;
      }
    }

    try {
      if (mode === "esborrar") {
        const resEliminar = await fetch(`${API_URL}/Eliminarcontacteentitat.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            idEmpresa: idEmpresa,
            idContacte: contact?.idContactes,
            idEntitat: idEntitat,
          }),
        });
        const dataEliminar = await resEliminar.json();
        if (dataEliminar.success) {
          onSave?.();
        } else {
          alert(`Error: ${dataEliminar.message || "No s'ha pogut eliminar la relació"}`);
        }
        return;
      }

      if (mode === "nou") {
        const emailNorm = email?.trim();
        const telefonNorm = telefon?.trim();
        const nomNorm = nom?.trim();
        const cognom1Norm = cognom1?.trim();
        const cognom2Norm = cognom2?.trim();

        let contacteExistent = null;
        let tipusCoincidencia = "";

        if (emailNorm || telefonNorm) {
          const resBuscar = await fetch(`${API_URL}/Buscarcontacte.php`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              idEmpresa: idEmpresa,
              nom: nomNorm || "",
              cognom1: cognom1Norm || "",
              cognom2: cognom2Norm || "",
              email: emailNorm || "",
              telefon: telefonNorm || "",
            }),
          });
          const dataBuscar = await resBuscar.json();

          if (dataBuscar.success && Array.isArray(dataBuscar.contactes) && dataBuscar.contactes.length > 0) {
            const candidatsFortes = dataBuscar.contactes.filter((c) => {
              const matchType = c.matchType || "";
              const score = c.score || 0;
              return (
                (matchType.includes("email") && score >= 60) ||
                (matchType.includes("phone") && score >= 70) ||
                (matchType.includes("whatsapp") && score >= 70)
              );
            });

            if (candidatsFortes.length > 0) {
              contacteExistent = candidatsFortes[0];
              if (contacteExistent.matchType?.includes("email")) {
                tipusCoincidencia = "email";
              } else if (
                contacteExistent.matchType?.includes("phone") ||
                contacteExistent.matchType?.includes("whatsapp")
              ) {
                tipusCoincidencia = "telèfon";
              } else {
                tipusCoincidencia = "dades similars";
              }
            }
          }
        }

        if (contacteExistent) {
          setContacteSimilar(contacteExistent);
          setTipusCoincidenciaSimilar(tipusCoincidencia);
          setShowModalSimilar(true);
          return;
        }

        const resNou = await fetch(`${API_URL}/Noucontacte.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            Nom: nom,
            Cognom1: cognom1,
            Cognom2: cognom2,
            Telefon: telefon,
            Email: email,
            Entitats_idEntitats: null,
            Empresa_idEmpresa: idEmpresa,
            Rol: rolPare,
          }),
        });
        const dataNou = await resNou.json();
        if (!dataNou.success) {
          alert(`Error: ${dataNou.message || "No s'ha pogut crear el contacte"}`);
          return;
        }

        const idContacteNou = dataNou.idContactes;
        const resRelacio = await fetch(`${API_URL}/Afegircontacteentitat.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            idEmpresa: idEmpresa,
            idContacte: idContacteNou,
            idEntitat: idEntitat,
          }),
        });
        const dataRelacio = await resRelacio.json();
        if (dataRelacio.success) {
          onSave?.();
        } else {
          alert(`Contacte creat però no s'ha pogut vincular: ${dataRelacio.message}`);
          onSave?.();
        }
        return;
      }

      if (mode === "modificar") {
        const resMod = await fetch(`${API_URL}/Modificarcontacte.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            idContactes: contact?.idContactes,
            Nom: nom,
            Cognom1: cognom1,
            Cognom2: cognom2,
            Telefon: telefon,
            Email: email,
          }),
        });
        const dataMod = await resMod.json();
        if (dataMod.success) {
          onSave?.();
        } else {
          alert(`Error: ${dataMod.message || "No s'ha pogut modificar el contacte"}`);
        }
        return;
      }

      onCancel?.();
    } catch (e) {
      console.error("Error guardant contacte:", e);
      alert("S'ha produït un error en el procés.");
    }
  };

  const handleVincularExistent = async () => {
    if (!contacteSimilar) return;

    try {
      const rolActual = contacteSimilar.Rol || "";
      let nouRol = rolActual;

      if (tipusPare === "Client") {
        if (rolActual === "Proveïdor" || rolActual === "Proveidor") {
          nouRol = "Ambdos";
        } else if (rolActual === "Altres") {
          nouRol = "Client";
        }
      } else if (tipusPare === "Proveïdor" || tipusPare === "Proveidor") {
        if (rolActual === "Client") {
          nouRol = "Ambdos";
        } else if (rolActual === "Altres") {
          nouRol = "Proveidor";
        }
      }

      if (nouRol !== rolActual) {
        const resMod = await fetch(`${API_URL}/Modificarcontacte.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            idContactes: contacteSimilar.idContactes,
            Rol: nouRol,
          }),
        });
        const dataMod = await resMod.json();
        if (!dataMod.success) {
          alert(`Error actualitzant el Rol: ${dataMod.message}`);
          return;
        }
      }

      const resRelacio = await fetch(`${API_URL}/Afegircontacteentitat.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          idEmpresa: idEmpresa,
          idContacte: contacteSimilar.idContactes,
          idEntitat: idEntitat,
        }),
      });
      const dataRelacio = await resRelacio.json();
      if (dataRelacio.success) {
        setShowModalSimilar(false);
        setContacteSimilar(null);
        const missatge =
          nouRol !== rolActual
            ? `Contacte vinculat correctament. Rol actualitzat a "${nouRol}".`
            : "Contacte existent vinculat correctament.";
        alert(missatge);
        onSave?.();
      } else {
        alert(`Error al vincular: ${dataRelacio.message}`);
      }
    } catch (e) {
      console.error("Error vinculant contacte:", e);
      alert("S'ha produït un error en vincular el contacte.");
    }
  };

  const handleCrearNou = async () => {
    setShowModalSimilar(false);
    setContacteSimilar(null);

    try {
      const resNou = await fetch(`${API_URL}/Noucontacte.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          Nom: nom,
          Cognom1: cognom1,
          Cognom2: cognom2,
          Telefon: telefon,
          Email: email,
          Entitats_idEntitats: null,
          Empresa_idEmpresa: idEmpresa,
          Rol: rolPare,
        }),
      });
      const dataNou = await resNou.json();
      if (!dataNou.success) {
        alert(`Error: ${dataNou.message || "No s'ha pogut crear el contacte"}`);
        return;
      }

      const idContacteNou = dataNou.idContactes;
      const resRelacio = await fetch(`${API_URL}/Afegircontacteentitat.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          idEmpresa: idEmpresa,
          idContacte: idContacteNou,
          idEntitat: idEntitat,
        }),
      });
      const dataRelacio = await resRelacio.json();
      if (dataRelacio.success) {
        onSave?.();
      } else {
        alert(`Contacte creat però no s'ha pogut vincular: ${dataRelacio.message}`);
        onSave?.();
      }
    } catch (e) {
      console.error("Error creant contacte:", e);
      alert("S'ha produït un error en crear el contacte.");
    }
  };

  const handleCancelarModal = () => {
    setShowModalSimilar(false);
    setContacteSimilar(null);
    setTipusCoincidenciaSimilar("");
  };

  const cognoms = [cognom1, cognom2].filter((x) => x && x.trim()).join(" ");
  const nomComplet = [cognoms, nom].filter((x) => x && x.trim()).join(", ");
  const potNavegar = Boolean(onPrev && onNext && navTotal > 1);
  const camp = (label, content, mida = "mig") => {
    const extra =
      mida === true || mida === "wide" || mida === "ample"
        ? "dv2-field--ample"
        : mida === "long" || mida === "llarg"
        ? "dv2-field--llarg"
        : mida === "curt"
        ? "dv2-field--curt"
        : "dv2-field--mig";
    return (
      <label className={`dv2-field ${extra}`}>
        <span className="dv2-label">{label}</span>
        {content}
      </label>
    );
  };

  const modalSimilarV2 = showModalSimilar && contacteSimilar && (
    <div
      className="dv2-modalOverlay"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) handleCancelarModal();
      }}
    >
      <div className="dv2-modal">
        <h3 className="dv2-modal-title">Contacte similar detectat</h3>
        <p className="dv2-modal-lead">
          S'ha detectat un contacte existent amb <strong>{tipusCoincidenciaSimilar}</strong>.
        </p>
        <div className="dv2-fields dv2-fields--sizes">
          {camp(
            "Nom",
            <span className="dv2-value">
              {[contacteSimilar.Cognom1, contacteSimilar.Cognom2, contacteSimilar.Nom]
                .filter(Boolean)
                .join(" ") || "—"}
            </span>
          )}
          {camp("Telèfon", <span className="dv2-value">{contacteSimilar.Telefon || "—"}</span>)}
          {camp("Email", <span className="dv2-value">{contacteSimilar.Email || "—"}</span>)}
        </div>
        <div className="dv2-modal-actions">
          <button type="button" className="dv2-btn" onClick={handleCancelarModal}>
            Cancel·lar
          </button>
          <button type="button" className="dv2-btn" onClick={handleCrearNou}>
            Crear nou
          </button>
          <button type="button" className="dv2-btn dv2-btn--primary" onClick={handleVincularExistent}>
            Vincular existent
          </button>
        </div>
      </div>
    </div>
  );

  if (v2) {
    return (
      <>
        {mode === "esborrar" && (
          <div className="dv2-banner dv2-banner--danger">
            Es desvincula el contacte d'aquest {etiquetaPare}. El contacte no s'elimina si està vinculat a altres entitats.
          </div>
        )}
        <div className="dv2-entity-body">
          <section className="dv2-card">
            <div className="dv2-card-head">
              <span className="dv2-card-num">1</span>
              <h2 className="dv2-card-title">Dades del contacte</h2>
            </div>
            <div className="dv2-fields dv2-fields--sizes">
              {isReadOnly ? (
                <>
                  {camp("Nom", <span className="dv2-value">{nomComplet || "—"}</span>, "ample")}
                  {camp("Telèfon", <span className="dv2-value">{telefon || "—"}</span>)}
                  {camp("Email", <span className="dv2-value">{email || "—"}</span>, "llarg")}
                </>
              ) : (
                <>
                  {camp(
                    "Nom",
                    <input className="dv2-input" type="text" value={nom} onChange={(e) => setNom(e.target.value)} />
                  )}
                  {camp(
                    "Cognom 1",
                    <input
                      className="dv2-input"
                      type="text"
                      value={cognom1}
                      onChange={(e) => setCognom1(e.target.value)}
                    />
                  )}
                  {camp(
                    "Cognom 2",
                    <input
                      className="dv2-input"
                      type="text"
                      value={cognom2}
                      onChange={(e) => setCognom2(e.target.value)}
                    />
                  )}
                  <span className="dv2-fields-break" aria-hidden="true" />
                  {camp(
                    "Telèfon",
                    <input
                      className="dv2-input"
                      type="text"
                      value={telefon}
                      onChange={(e) => setTelefon(e.target.value)}
                    />
                  )}
                  {camp(
                    "Email",
                    <input
                      className="dv2-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />,
                    "llarg"
                  )}
                </>
              )}
            </div>
          </section>
        </div>
        {(potNavegar || mode === "nou" || mode === "modificar" || mode === "esborrar") && (
          <div className="dv2-entity-dock">
            {potNavegar && (
              <div className="dv2-recordNav">
                <button type="button" className="dv2-btn dv2-recordNav-btn" onClick={onPrev} title="Anterior">
                  ‹
                </button>
                <span className="dv2-recordNav-info">
                  {navIndex} / {navTotal}
                </span>
                <button type="button" className="dv2-btn dv2-recordNav-btn" onClick={onNext} title="Següent">
                  ›
                </button>
              </div>
            )}
            {(mode === "nou" || mode === "modificar" || mode === "esborrar") && (
              <div className="dv2-footerActions">
                <button type="button" className="dv2-btn" onClick={onCancel}>
                  Cancel·lar
                </button>
                {mode === "esborrar" ? (
                  <button type="button" className="dv2-btn dv2-btn--danger" onClick={handleSubmit}>
                    Esborrar contacte
                  </button>
                ) : (
                  <button type="button" className="dv2-btn dv2-btn--primary" onClick={handleSubmit}>
                    {mode === "nou" ? "Crear contacte" : "Desar"}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
        {modalSimilarV2}
      </>
    );
  }

  if (isReadOnly) {
    return (
      <>
        <div className="Dashboard-form-section">
          <div className="info-inline"><strong>Nom:</strong> {nomComplet || "—"}</div>
        </div>

        <div className="Dashboard-form-section">
          <div className="info-inline"><strong>Telèfon:</strong> {telefon || "—"}</div>
          <div className="info-inline"><strong>Email:</strong> {email || "—"}</div>
        </div>

        {mode === "esborrar" && (
          <div className="Dashboard-button-group">
            <button type="button" className="Dashboard-btn Dashboard-btn-acceptar" onClick={handleSubmit}>
              Confirma esborrat
            </button>
            <button type="button" className="Dashboard-btn Dashboard-btn-cancelar" onClick={onCancel}>
              Cancel·lar
            </button>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <form className="Dashboard-form" style={{ marginTop: "20px" }}>
        <div className="Dashboard-form-row">
          <label>
            Nom:
            <input type="text" value={nom} onChange={(e) => setNom(e.target.value)} />
          </label>
          <label>
            Cognom1:
            <input type="text" value={cognom1} onChange={(e) => setCognom1(e.target.value)} />
          </label>
          <label>
            Cognom2:
            <input type="text" value={cognom2} onChange={(e) => setCognom2(e.target.value)} />
          </label>
        </div>

        <div className="Dashboard-form-row">
          <label>
            Telèfon:
            <input type="text" value={telefon} onChange={(e) => setTelefon(e.target.value)} />
          </label>
          <label>
            Email:
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
        </div>

        <div className="Dashboard-button-group">
          <button type="button" className="Dashboard-btn Dashboard-btn-acceptar" onClick={handleSubmit}>
            {mode === "nou" ? "Crear" : "Guardar"}
          </button>
          <button type="button" className="Dashboard-btn Dashboard-btn-cancelar" onClick={onCancel}>
            Cancel·lar
          </button>
        </div>
      </form>

      {showModalSimilar && contacteSimilar && (
        <div className="Dashboard-modal-overlay" onClick={handleCancelarModal}>
          <div className="Dashboard-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="Dashboard-modal-header">
              <h3>Contacte similar detectat</h3>
              <button className="Dashboard-modal-close" onClick={handleCancelarModal}>
                ✕
              </button>
            </div>

            <div className="Dashboard-modal-body">
              <p style={{ marginBottom: "15px" }}>
                S'ha detectat un contacte existent amb <strong>{tipusCoincidenciaSimilar}</strong>:
              </p>

              <div className="Dashboard-form-section" style={{ backgroundColor: "#f5f5f5", padding: "15px", borderRadius: "4px" }}>
                <div className="info-inline">
                  <strong>Nom:</strong>{" "}
                  {[contacteSimilar.Cognom1, contacteSimilar.Cognom2, contacteSimilar.Nom]
                    .filter(Boolean)
                    .join(" ") || "—"}
                </div>
                <div className="info-inline">
                  <strong>Telèfon:</strong> {contacteSimilar.Telefon || "—"}
                </div>
                <div className="info-inline">
                  <strong>Email:</strong> {contacteSimilar.Email || "—"}
                </div>
              </div>

              <p style={{ marginTop: "15px", fontSize: "0.95em", color: "#666" }}>
                Què vols fer?
              </p>
            </div>

            <div className="Dashboard-modal-footer">
              <button type="button" className="Dashboard-btn Dashboard-btn-acceptar" onClick={handleVincularExistent}>
                Vincular existent
              </button>
              <button
                type="button"
                className="Dashboard-btn Dashboard-btn-secundari"
                onClick={handleCrearNou}
                style={{ backgroundColor: "#6c757d" }}
              >
                Crear nou
              </button>
              <button type="button" className="Dashboard-btn Dashboard-btn-cancelar" onClick={handleCancelarModal}>
                Cancel·lar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Contactes;
